import { Button as BaseButton } from "@base-ui/react/button";
import loadingIcon from "@loot-os/icons/icons/loading";
import type { IconData } from "@loot-os/icons/types";
import type { ComponentProps, ReactNode } from "react";
import { buttonStyles } from "./Button.styles.js";
import { Icon } from "./Icon.js";
import { useIconsStill } from "./iconStill.js";

type Variants = NonNullable<Parameters<typeof buttonStyles>[0]>;

export interface ButtonProps extends Omit<ComponentProps<typeof BaseButton>, "className"> {
  variant?: Variants["variant"];
  size?: Variants["size"];
  fullWidth?: boolean;
  /**
   * An icon before the label. It plays on hover where the theme animates every icon
   * (`icons.motion: "all"`), except on a commit: a form's submit, or a button in a dialog's footer.
   */
  icon?: IconData;
  /** Something after the label: a price, a count, a chevron. */
  trailing?: ReactNode;
  /**
   * Set when `render` is a `<button>`. It defaults to true without `render` and false with it, so
   * rendering through another element (a `<div>` in a composite) keeps Base UI from adding
   * native-button attributes.
   */
  nativeButton?: boolean;
  /**
   * Working on the action: shows a spinner in place of the icon, sets `aria-busy` and ignores
   * presses, while keeping the label (and so the button's name and width) as it was.
   */
  loading?: boolean;
  /**
   * Marks the one action a view most wants taken (Deposit, a banner's call to action, Create
   * battle): a light sweeps across it once as the pointer arrives. Never on a dialog's or a form's
   * commit, whose icon stays still.
   * Still under reduced motion and on touch.
   */
  shine?: boolean;
  className?: string;
}

/**
 * Triggers an action. Disabled stays focusable (`focusableWhenDisabled`), so a keyboard user
 * can still reach it and hear why it can't be used.
 */
export function Button({
  variant,
  size,
  fullWidth,
  icon,
  trailing,
  loading = false,
  shine = false,
  disabled,
  className,
  children,
  onClick,
  render,
  nativeButton = render === undefined,
  type,
  ...props
}: ButtonProps) {
  // A commit's icon stays still: a form's submit, or a button in a dialog's footer.
  const commit = useIconsStill() || type === "submit";
  return (
    <BaseButton
      render={render}
      nativeButton={nativeButton}
      focusableWhenDisabled
      disabled={disabled}
      type={type}
      aria-busy={loading || undefined}
      className={buttonStyles({ variant, size, fullWidth, className })}
      onClick={(event) => {
        // A busy submit button must not submit its form again.
        if (loading) event.preventDefault();
        else onClick?.(event);
      }}
      {...props}
    >
      {loading ? (
        <Icon icon={loadingIcon} className="animate-spin motion-reduce:animate-none" />
      ) : (
        // Plays on hover as the theme's icons allow; a commit's stays still.
        icon && <Icon icon={icon} trigger={commit ? undefined : "hover"} />
      )}
      {children}
      {trailing}
      {shine && <span aria-hidden className="shine-sweep" />}
    </BaseButton>
  );
}
